import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save,
  Plus,
  Trash2,
  User,
  Package,
  Clock,
  ArrowLeft,
  ChevronDown,
  UserCheck,
  CheckCircle2,
  X
} from 'lucide-react';
import { Order, Make, Model, ServiceType, Technician, CartItem, OrderStatusUpdateRequest, OrderReplacement } from '../types';
import { RefreshCw, MessageSquare } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const SectionHeader: React.FC<{ num: number; title: string; icon: React.ReactNode; color: string }> = ({ num, title, icon, color }) => (
  <div className={`px-6 py-3 border-b border-gray-200 flex items-center gap-3 ${color}`}>
    <span className="w-7 h-7 rounded-full bg-white/80 text-gray-800 text-sm font-bold flex items-center justify-center border border-gray-300 shadow-sm">{num}</span>
    <div className="flex items-center gap-2">
      {icon}
      <h2 className="font-semibold text-gray-800">{title}</h2>
    </div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className }) => (
  <div className={className}>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const disabledInputCls = `${inputCls} bg-gray-50 cursor-not-allowed`;

const CreateOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [orderType, setOrderType] = useState('Online');
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [orderStatus, setOrderStatus] = useState('Created');
  const isPaymentReceived = orderStatus === 'Payment Received';

  const [makes, setMakes] = useState<Make[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [services, setServices] = useState<ServiceType[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);

  const [selectedMake, setSelectedMake] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedTechnician, setSelectedTechnician] = useState<number | null>(null);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [editableServiceCost, setEditableServiceCost] = useState<number | null>(null);

  const [orderTotals, setOrderTotals] = useState<{
    totalItemPrice: number;
    totalServiceCost: number;
    gstAmount: number;
    finalAmount: number;
  } | null>(null);

  const [isGstInvoice, setIsGstInvoice] = useState(false);
  const [customerGstin, setCustomerGstin] = useState('');
  const [customerCompanyName, setCustomerCompanyName] = useState('');

  // Replacement state
  const [replacements, setReplacements] = useState<OrderReplacement[]>([]);
  const [showReplacementForm, setShowReplacementForm] = useState(false);
  const [replacementForm, setReplacementForm] = useState<Partial<OrderReplacement>>({
    replacementCost: 0,
    replacementDate: new Date().toISOString().split('T')[0],
    replacementStatus: 'Pending',
    notes: '',
  });
  const [editingReplacementId, setEditingReplacementId] = useState<number | null>(null);
  const [replacementLoading, setReplacementLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [savingItemPriceId, setSavingItemPriceId] = useState<number | null>(null);

  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [showMakeDropdown, setShowMakeDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [filteredMakes, setFilteredMakes] = useState<Make[]>([]);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);

  // Inline price editing state for Available Services
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [draftActualPrice, setDraftActualPrice] = useState<string>('');
  const [draftMarginalPrice, setDraftMarginalPrice] = useState<string>('');

  useEffect(() => {
    loadMakes();
    loadTechnicians();
    if (isEditMode && id) {
      loadOrderDetails(parseInt(id));
    }
  }, [isEditMode, id]);

  useEffect(() => {
    if (makeSearch) {
      setFilteredMakes(makes.filter(m => m.name.toLowerCase().includes(makeSearch.toLowerCase())));
    } else {
      setFilteredMakes(makes);
    }
  }, [makes, makeSearch]);

  useEffect(() => {
    if (modelSearch) {
      setFilteredModels(models.filter(m => m.name.toLowerCase().includes(modelSearch.toLowerCase())));
    } else {
      setFilteredModels(models);
    }
  }, [models, modelSearch]);

  const handleMakeSelect = (make: Make) => {
    setSelectedMake(make.id);
    setMakeSearch(make.name);
    setShowMakeDropdown(false);
    setSelectedModel(null);
    setModelSearch('');
    setServices([]);
    setCart([]);
    setTimeout(() => loadModels(make.id), 0);
  };

  const handleModelSelect = (model: Model) => {
    setSelectedModel(model);
    setModelSearch(model.name);
    setShowModelDropdown(false);
    setTimeout(() => loadServices(model.id), 0);
  };

  const loadMakes = async () => {
    try {
      const response = await apiService.getMakes();
      if (response.isSuccess) {
        setMakes(response.entity || []);
        setFilteredMakes(response.entity || []);
      } else {
        toast.error('Failed to load makes');
      }
    } catch (error) {
      toast.error('Error loading makes');
    }
  };

  const loadModels = async (makeId: number) => {
    try {
      setSelectedModel(null);
      setServices([]);
      setCart([]);
      const response = await apiService.getModels(makeId);
      if (response.isSuccess) {
        setModels(response.entity || []);
        setFilteredModels(response.entity || []);
      } else {
        toast.error('Failed to load models');
      }
    } catch (error) {
      toast.error('Error loading models');
    }
  };

  const loadServices = async (modelId: number) => {
    try {
      setCart([]);
      const response = await apiService.getServiceTypes(modelId);
      if (response.isSuccess) {
        setServices(response.entity || []);
      } else {
        toast.error('Failed to load services');
      }
    } catch (error) {
      toast.error('Error loading services');
    }
  };

  const loadTechnicians = async () => {
    try {
      const response = await apiService.getTechnicians();
      if (response.isSuccess) {
        setTechnicians(response.entity || []);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const loadOrderDetails = async (orderId: number) => {
    setLoadingOrder(true);
    try {
      const response = await apiService.getOrder(orderId);
      if (response.isSuccess && response.entity) {
        const order = response.entity;
        const orderTypeValue = (order as any).OrderType ?? (order as any).orderType ?? 'Online';
        setOrderType(orderTypeValue);
        setCustomerName(order.customerName);
        setCustomerMobile(order.customerMobile);
        setCustomerAddress(order.customerAddress);
        setOrderDate(order.orderDate.toString().split('T')[0]);
        setPaymentMethod(order.paymentMethod);
        setOrderStatus(order.orderStatus);
        setSelectedMake(order.makeId);
        setSelectedTechnician(order.technicianId);
        setDiscountAmount(order.discountAmount);
        setIsGstInvoice(order.isGstInvoice ?? false);
        setCustomerGstin(order.customerGstin ?? '');
        setCustomerCompanyName(order.customerCompanyName ?? '');

        setOrderTotals({
          totalItemPrice: order.totalItemPrice || 0,
          totalServiceCost: order.totalServiceCost || 0,
          gstAmount: order.gstAmount || 0,
          finalAmount: order.finalAmount || 0
        });
        setEditableServiceCost(order.totalServiceCost || 0);

        const selectedMakeObj = makes.find(m => m.id === order.makeId);
        if (selectedMakeObj) setMakeSearch(selectedMakeObj.name);

        if (order.makeId && order.modelId) {
          const modelsResponse = await apiService.getModels(order.makeId);
          if (modelsResponse.isSuccess) {
            setModels(modelsResponse.entity || []);
            setFilteredModels(modelsResponse.entity || []);
            const model = modelsResponse.entity?.find(m => m.id === order.modelId);
            if (model) {
              setSelectedModel(model);
              setModelSearch(model.name);
              const servicesResponse = await apiService.getServiceTypes(order.modelId);
              if (servicesResponse.isSuccess) {
                const servicesData = servicesResponse.entity || [];
                setServices(servicesData);

                if (order.orderItems) {
                  const cartItems = order.orderItems.map(item => {
                    const svc = servicesData.find(s => s.id === item.serviceId);
                    // Prefer snapshot values stored in order_items; fall back to services table
                    const partPrice = (item.partPrice != null && item.partPrice > 0)
                      ? item.partPrice
                      : (item.actualPrice - (svc?.marginalCost ?? 0));
                    const marginalCost = (item.marginalPrice != null && item.marginalPrice > 0)
                      ? item.marginalPrice
                      : (svc?.marginalCost ?? 0);
                    return {
                      id: item.serviceId,
                      serviceName: item.serviceName,
                      actualPrice: partPrice + marginalCost,
                      serviceCost: item.serviceCost,
                      quantity: item.quantity,
                      serviceType: 'Repair',
                      warrantyPeriod: item.warrantyPeriod,
                      modelId: order.modelId,
                      serviceImage: '',
                      serviceDuration: 60,
                      marginalCost,
                      markedPrice: (partPrice + marginalCost).toString(),
                      makeId: order.makeId,
                      makeName: order.makeName,
                      modelName: order.modelName,
                      warrantyCoveredItems: item.warrantyCoveredItems || '',
                      warrantyNotCoveredItems: item.warrantyNotCoveredItems || '',
                      warrantyTypeDescription: item.warrantyTypeDescription || '',
                      warrantyName: item.warrantyName || '',
                      warrantyTypeId: item.warrantyTypeId || 1,
                      warrantyEndDate: item.warrantyEndDate || new Date()
                    };
                  });
                  setCart(cartItems as CartItem[]);
                }
              }
            }
          }
        }
      }
      // Load replacements
      try {
        const repResult = await apiService.getReplacementsByOrder(orderId);
        if (repResult.isSuccess) setReplacements(repResult.entity || []);
      } catch {}

    } catch (error) {
      toast.error('Error loading order details');
    } finally {
      setLoadingOrder(false);
    }
  };

  const loadReplacements = async () => {
    if (!id) return;
    try {
      const repResult = await apiService.getReplacementsByOrder(Number(id));
      if (repResult.isSuccess) setReplacements(repResult.entity || []);
    } catch {}
  };

  const saveReplacement = async () => {
    if (!id) return;
    setReplacementLoading(true);
    try {
      const payload: OrderReplacement = {
        ...(replacementForm as OrderReplacement),
        orderId: Number(id),
        technicianId: replacementForm.technicianId || undefined,
      };

      let result;
      if (editingReplacementId) {
        result = await apiService.updateReplacement(editingReplacementId, { ...payload, id: editingReplacementId });
      } else {
        result = await apiService.createReplacement(payload);
      }

      if (result.isSuccess) {
        toast.success(editingReplacementId ? 'Replacement updated' : 'Replacement added');
        setShowReplacementForm(false);
        setEditingReplacementId(null);
        setReplacementForm({ replacementCost: 0, replacementDate: new Date().toISOString().split('T')[0], replacementStatus: 'Pending', notes: '' });
        await loadReplacements();
      } else {
        toast.error(result.message || 'Failed to save replacement');
      }
    } catch {
      toast.error('Error saving replacement');
    } finally {
      setReplacementLoading(false);
    }
  };

  const deleteReplacement = async (repId: number) => {
    if (!window.confirm('Delete this replacement?')) return;
    try {
      const result = await apiService.deleteReplacement(repId);
      if (result.isSuccess) {
        toast.success('Replacement deleted');
        await loadReplacements();
      }
    } catch { toast.error('Error deleting replacement'); }
  };

  const sendReplacementMessage = async (repId: number) => {
    try {
      const result = await apiService.sendReplacementMessage(repId);
      if (result.isSuccess) {
        toast.success('Message sent to customer');
        await loadReplacements();
      } else {
        toast.error(result.message || 'Failed to send message');
      }
    } catch { toast.error('Error sending message'); }
  };

  const startEditReplacement = (rep: OrderReplacement) => {
    setEditingReplacementId(rep.id!);
    setReplacementForm({
      replacementCost: rep.replacementCost,
      technicianId: rep.technicianId,
      technicianName: rep.technicianName,
      replacementDate: rep.replacementDate?.toString().split('T')[0],
      replacementStatus: rep.replacementStatus,
      notes: rep.notes,
    });
    setShowReplacementForm(true);
  };

  const addToCart = (service: ServiceType) => {
    const existingItem = cart.find(item => item.id === service.id);
    if (existingItem) {
      removeFromCart(service.id);
      return;
    }
    setCart([...cart, { ...service, quantity: 1 }]);
    if (!isEditMode) setEditableServiceCost(null);
    toast.success('Service added');
  };

  const removeFromCart = (serviceId: number) => {
    setCart(cart.filter(item => item.id !== serviceId));
    if (!isEditMode) setEditableServiceCost(null);
  };

  const updateQuantity = (serviceId: number, quantity: number) => {
    if (quantity < 1) return;
    setCart(cart.map(item => item.id === serviceId ? { ...item, quantity } : item));
    if (!isEditMode) setEditableServiceCost(null);
  };

  const updateActualPrice = (serviceId: number, price: number) => {
    setCart(cart.map(item => item.id === serviceId ? { ...item, actualPrice: price } : item));
  };

  const calculateTotals = () => {
    let totalItemPrice: number;
    let totalServiceCost: number;

    if (isEditMode && orderTotals) {
      totalItemPrice = cart.length > 0
        ? cart.reduce((sum, item) => sum + (item.actualPrice * item.quantity), 0)
        : orderTotals.totalItemPrice;
      totalServiceCost = editableServiceCost !== null ? editableServiceCost : orderTotals.totalServiceCost;
    } else {
      totalItemPrice = cart.reduce((sum, item) => sum + (item.actualPrice * item.quantity), 0);
      totalServiceCost = editableServiceCost !== null
        ? editableServiceCost
        : cart.reduce((sum, item) => sum + (item.serviceCost * item.quantity), 0);
    }

    const discountedItemPrice = Math.max(totalItemPrice - discountAmount, 0);
    const gstAmount = (totalServiceCost * 18) / 100;
    const finalAmount = discountedItemPrice + totalServiceCost + gstAmount;

    return { totalItemPrice, totalServiceCost, gstAmount, finalAmount };
  };

  const saveOrderItemPrice = async (serviceId: number, newPrice: number) => {
    if (!id) return;
    setSavingItemPriceId(serviceId);
    try {
      const response = await apiService.updateOrderItemPrice(parseInt(id), serviceId, newPrice);
      if (response.isSuccess) {
        toast.success('Price updated in order & service');
      } else {
        toast.error('Failed to update price');
      }
    } catch {
      toast.error('Error updating price');
    } finally {
      setSavingItemPriceId(null);
    }
  };

  const saveServicePrice = async (serviceId: number) => {
    const newActual = parseFloat(draftActualPrice);
    const newMarginal = parseFloat(draftMarginalPrice);
    if (isNaN(newActual) || newActual < 0 || isNaN(newMarginal) || newMarginal < 0) {
      toast.error('Enter valid prices');
      return;
    }
    try {
      const response = await apiService.updateServicePrice(serviceId, newActual, newMarginal);
      if (response.isSuccess) {
        // API returns actualPrice as actual+marginal combined; update both locally
        setServices(prev => prev.map(s => s.id === serviceId
          ? { ...s, actualPrice: newActual + newMarginal, marginalCost: newMarginal }
          : s
        ));
        setCart(prev => prev.map(item => item.id === serviceId
          ? { ...item, actualPrice: newActual + newMarginal, marginalCost: newMarginal }
          : item
        ));
        setEditingPriceId(null);
        toast.success('Price updated');
      } else {
        toast.error('Failed to update price');
      }
    } catch {
      toast.error('Error updating price');
    }
  };

  const validateForm = () => {
    if (!customerName.trim()) { toast.error('Customer name is required'); return false; }
    if (!customerMobile.trim() || customerMobile.length !== 10) { toast.error('Valid 10-digit mobile number is required'); return false; }
    if (!customerAddress.trim()) { toast.error('Customer address is required'); return false; }
    if (!selectedMake) { toast.error('Make selection is required'); return false; }
    if (!selectedModel) { toast.error('Model selection is required'); return false; }
    if (cart.length === 0) { toast.error('At least one service must be selected'); return false; }
    if (!paymentMethod) { toast.error('Payment method is required'); return false; }
    if (isGstInvoice && customerGstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(customerGstin)) {
      toast.error('Invalid GSTIN format (e.g. 27AABCM1234A1Z5)');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const totals = calculateTotals();
      const orderItems = cart.map((item) => ({
        id: 0,
        orderId: 0,
        serviceId: item.id,
        serviceName: item.serviceName,
        serviceType: item.serviceType || 'Repair',
        actualPrice: item.actualPrice,
        partPrice: item.actualPrice - (item.marginalCost || 0),
        marginalPrice: item.marginalCost || 0,
        serviceCost: item.serviceCost,
        quantity: item.quantity,
        itemTotal: item.actualPrice * item.quantity,
        serviceStatus: 'Pending',
        notes: '',
        warrantyPeriod: item.warrantyPeriod,
        warrantyEndDate: item.warrantyEndDate || new Date(Date.now() + item.warrantyPeriod * 24 * 60 * 60 * 1000),
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 1,
        warrantyTypeId: item.warrantyTypeId || 1,
        warrantyName: item.warrantyName || '',
        warrantyTypeDescription: item.warrantyTypeDescription || '',
        warrantyCoveredItems: item.warrantyCoveredItems || '',
        warrantyNotCoveredItems: item.warrantyNotCoveredItems || '',
        serviceImage: item.serviceImage || ''
      }));

      const orderData: Partial<Order> = {
        customerName, customerMobile, customerEmail: '', customerAddress,
        makeId: selectedMake!,
        makeName: makes.find(m => m.id === selectedMake)?.name || '',
        modelId: selectedModel!.id,
        modelName: selectedModel!.name,
        orderDate: new Date(orderDate),
        orderStatus: isEditMode ? orderStatus : 'Created',
        OrderType: orderType,
        totalItemPrice: totals.totalItemPrice,
        totalServiceCost: totals.totalServiceCost,
        discountAmount, gstPercentage: 18,
        gstAmount: totals.gstAmount,
        finalAmount: totals.finalAmount,
        paymentStatus: 'Pending', paymentMethod, amountPaid: 0,
        balanceAmount: totals.finalAmount,
        technicianId: selectedTechnician || 0,
        isGstInvoice,
        customerGstin: isGstInvoice ? customerGstin : '',
        customerCompanyName: isGstInvoice ? customerCompanyName : '',
        notes: '', deviceCondition: 'Good',
        warrantyPeriod: Math.max(...orderItems.map(i => i.warrantyPeriod || 0), 0),
        warrantyEndDate: new Date(Date.now() + Math.max(...orderItems.map(i => i.warrantyPeriod || 0), 0) * 24 * 60 * 60 * 1000),
        createdBy: 1, updatedBy: 1, isActive: true, orderItems
      };

      const response = isEditMode && id
        ? await apiService.updateOrder(parseInt(id), orderData as Order)
        : await apiService.createOrder(orderData as Order);

      if (response.isSuccess) {
        toast.success(`Order ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate('/orders');
      } else {
        toast.error(response.errors?.join(', ') || `Failed to ${isEditMode ? 'update' : 'create'} order`);
      }
    } catch (error) {
      toast.error('Error saving order');
    } finally {
      setLoading(false);
    }
  };

  const assignTechnician = async () => {
    if (!selectedTechnician || !id) return;
    try {
      const response = await apiService.assignTechnicianToOrder(parseInt(id), selectedTechnician);
      if (response.isSuccess) {
        toast.success('Technician assigned successfully');
        setOrderStatus('Assigned');
      } else {
        toast.error('Failed to assign technician');
      }
    } catch (error) {
      toast.error('Error assigning technician');
    }
  };

  const updateOrderStatus = async () => {
    if (!id || !orderStatus) return;
    try {
      const request: OrderStatusUpdateRequest = { status: orderStatus, notes: '', userId: 1 };
      const response = await apiService.updateOrderStatus(parseInt(id), request);
      if (response.isSuccess) {
        toast.success('Order status updated successfully');
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to update order status');
      }
    } catch (error) {
      toast.error('Error updating order status');
    }
  };

  const totals = calculateTotals();
  const displayedFinalAmount = isEditMode && orderTotals
    ? orderTotals.finalAmount
    : totals.finalAmount;

  if (loadingOrder) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto pb-24 space-y-5">
      {/* ── Page Header ── */}
      <div className="flex items-center justify-between py-4 border-b border-gray-200 sticky top-0 bg-gray-50 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/orders')} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isEditMode ? `Edit Order #${id}` : 'New Order'}
            </h1>
            <p className="text-xs text-gray-400">{isEditMode ? 'Modify order details below' : 'Fill in the details to create an order'}</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════ */}
      {/* Section 1 — Customer Information                       */}
      {/* ══════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <SectionHeader num={1} title="Customer Information" icon={<User className="h-4 w-4 text-blue-600" />} color="bg-blue-50" />
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Order Type">
              <select value={orderType} onChange={(e) => setOrderType(e.target.value)} className={inputCls}>
                <option value="Online">Online</option>
                <option value="Offline">Offline / Walk-in</option>
              </select>
            </Field>
            <Field label="Customer Name" className="md:col-span-2">
              <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Full name" className={inputCls} />
            </Field>
            <Field label="Mobile Number">
              <input type="tel" value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit number" className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Address" className="md:col-span-2">
              <textarea value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder="Customer address" rows={2} className={inputCls} />
            </Field>
            <Field label="Order Date">
              <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className={inputCls} />
            </Field>
          </div>

          {/* GST Invoice Toggle */}
          <div className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg bg-gray-50">
            <div>
              <p className="text-sm font-medium text-gray-800">GST Invoice Required</p>
              <p className="text-xs text-gray-400 mt-0.5">Generate CGST + SGST tax invoice</p>
            </div>
            <button
              type="button"
              onClick={() => setIsGstInvoice(!isGstInvoice)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${isGstInvoice ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${isGstInvoice ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {isGstInvoice && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-1">
              <Field label="Company / Business Name">
                <input type="text" value={customerCompanyName} onChange={(e) => setCustomerCompanyName(e.target.value)} placeholder="Company name" className={inputCls} />
              </Field>
              <Field label="Customer GSTIN">
                <input type="text" value={customerGstin} onChange={(e) => setCustomerGstin(e.target.value.toUpperCase().slice(0, 15))} placeholder="e.g. 27AABCM1234A1Z5" maxLength={15} className={`${inputCls} font-mono tracking-wider`} />
                <p className="text-xs text-gray-400 mt-1">15-character GSTIN</p>
              </Field>
            </div>
          )}

          {!isEditMode && (
            <Field label="Payment Method" className="max-w-xs">
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={inputCls}>
                <option value="COD">Cash on Delivery (COD)</option>
                <option value="Online">Online Payment</option>
              </select>
            </Field>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════ */}
      {/* Section 2 — Order / Services                          */}
      {/* ══════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <SectionHeader num={2} title="Order Information" icon={<Package className="h-4 w-4 text-purple-600" />} color="bg-purple-50 rounded-t-xl" />
        <div className="p-6 space-y-6">

          {/* Make + Model */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Brand / Make">
              <div className="relative">
                <input
                  type="text"
                  value={makeSearch}
                  onChange={(e) => { setMakeSearch(e.target.value); setShowMakeDropdown(true); }}
                  onFocus={() => setShowMakeDropdown(true)}
                  onClick={() => { setShowMakeDropdown(true); if (!makeSearch) setFilteredMakes(makes); }}
                  onBlur={() => setTimeout(() => setShowMakeDropdown(false), 300)}
                  placeholder="Search brand..."
                  className={`${inputCls} pr-10`}
                />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                {showMakeDropdown && filteredMakes.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {filteredMakes.map((make) => (
                      <button
                        key={make.id}
                        onMouseDown={(e) => { e.preventDefault(); handleMakeSelect(make); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-2"
                      >
                        {make.imagePath && (
                          <img src={apiService.getFullImageUrl(make.imagePath)} alt={make.name} className="w-5 h-5 rounded object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        <span className="font-medium text-gray-800">{make.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Field>

            <Field label="Model">
              <div className="relative">
                <input
                  type="text"
                  value={modelSearch}
                  onChange={(e) => { setModelSearch(e.target.value); setShowModelDropdown(true); }}
                  onFocus={() => setShowModelDropdown(true)}
                  onClick={() => { if (selectedMake) { setShowModelDropdown(true); if (!modelSearch) setFilteredModels(models); } }}
                  onBlur={() => setTimeout(() => setShowModelDropdown(false), 300)}
                  placeholder={selectedMake ? 'Search model...' : 'Select brand first'}
                  disabled={!selectedMake}
                  className={selectedMake ? `${inputCls} pr-10` : `${disabledInputCls} pr-10`}
                />
                <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none ${selectedMake ? 'text-gray-400' : 'text-gray-300'}`} />
                {showModelDropdown && filteredModels.length > 0 && selectedMake && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {filteredModels.map((model) => (
                      <button
                        key={model.id}
                        onMouseDown={(e) => { e.preventDefault(); handleModelSelect(model); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-2"
                      >
                        {model.imagePath && (
                          <img src={apiService.getFullImageUrl(model.imagePath)} alt={model.name} className="w-5 h-5 rounded object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        <span className="font-medium text-gray-800">{model.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Field>
          </div>

          {/* Available Services */}
          {selectedModel && services.length > 0 && !isPaymentReceived && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Available Services for {selectedModel.name}
              </p>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wide border-b border-gray-200">
                      <th className="px-4 py-2.5 text-left">Service</th>
                      <th className="px-4 py-2.5 text-right">Parts Price</th>
                      <th className="px-4 py-2.5 text-right">Marginal Price</th>
                      <th className="px-4 py-2.5 text-right">Final Price</th>
                      <th className="px-4 py-2.5 text-center w-28"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {services.map((service) => {
                      const isInCart = cart.some(item => item.id === service.id);
                      const marginal = service.marginalCost ?? 0;
                      const rawActual = service.actualPrice - marginal;
                      const isEditing = editingPriceId === service.id;
                      return (
                        <tr key={service.id} className={`transition-colors ${isInCart ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                          {/* Service name */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                                {service.serviceImage
                                  ? <img src={apiService.getFullImageUrl(service.serviceImage)} alt={service.serviceName} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                  : <Package className="h-3.5 w-3.5 text-gray-400" />
                                }
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{service.serviceName}</p>
                                <p className="text-xs text-gray-400">{service.serviceDuration} min · {service.warrantyPeriod}d warranty</p>
                              </div>
                            </div>
                          </td>

                          {/* Parts Price (procurement) */}
                          <td className="px-4 py-3 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                value={draftActualPrice}
                                onChange={(e) => setDraftActualPrice(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveServicePrice(service.id); if (e.key === 'Escape') setEditingPriceId(null); }}
                                autoFocus
                                className="w-24 px-2 py-1 border border-blue-400 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <button
                                onClick={() => { setEditingPriceId(service.id); setDraftActualPrice(String(rawActual)); setDraftMarginalPrice(String(marginal)); }}
                                className="font-medium text-gray-800 hover:text-blue-700 hover:underline"
                                title="Click to edit"
                              >
                                ₹{rawActual.toLocaleString()}
                              </button>
                            )}
                          </td>

                          {/* Marginal Price */}
                          <td className="px-4 py-3 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                value={draftMarginalPrice}
                                onChange={(e) => setDraftMarginalPrice(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveServicePrice(service.id); if (e.key === 'Escape') setEditingPriceId(null); }}
                                className="w-24 px-2 py-1 border border-blue-400 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <button
                                onClick={() => { setEditingPriceId(service.id); setDraftActualPrice(String(rawActual)); setDraftMarginalPrice(String(marginal)); }}
                                className="font-medium text-gray-800 hover:text-blue-700 hover:underline"
                                title="Click to edit"
                              >
                                ₹{marginal.toLocaleString()}
                              </button>
                            )}
                          </td>

                          {/* Final Price (read-only) */}
                          <td className="px-4 py-3 text-right font-bold text-blue-700">
                            {isEditing
                              ? `₹${((parseFloat(draftActualPrice) || 0) + (parseFloat(draftMarginalPrice) || 0)).toLocaleString()}`
                              : `₹${service.actualPrice.toLocaleString()}`
                            }
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3 text-center">
                            {isEditing ? (
                              <div className="flex items-center gap-1 justify-center">
                                <button onClick={() => saveServicePrice(service.id)} className="px-2.5 py-1 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700">Save</button>
                                <button onClick={() => setEditingPriceId(null)} className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-200">✕</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => addToCart(service)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors mx-auto ${
                                  isInCart
                                    ? 'bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                              >
                                {isInCart
                                  ? <><CheckCircle2 className="h-3.5 w-3.5" /> Added</>
                                  : <><Plus className="h-3.5 w-3.5" /> Add</>
                                }
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedModel && services.length === 0 && (
            <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-lg">
              <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No services available for this model</p>
            </div>
          )}

          {/* ── Selected Services Billing Table ── */}
          {(cart.length > 0 || (isEditMode && orderTotals)) && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected Services</p>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600 text-xs font-semibold uppercase tracking-wide">
                      <th className="px-4 py-2.5 text-left w-8">#</th>
                      <th className="px-4 py-2.5 text-left">Service</th>
                      <th className="px-4 py-2.5 text-right">Parts Price</th>
                      <th className="px-4 py-2.5 text-right">Marginal Price</th>
                      <th className="px-4 py-2.5 text-right">Final Price</th>
                      <th className="px-4 py-2.5 text-right">Qty</th>
                      <th className="px-4 py-2.5 text-right">Subtotal</th>
                      <th className="px-3 py-2.5 text-center w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {cart.map((item, idx) => (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-400 font-medium">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{item.serviceName}</p>
                          <p className="text-xs text-gray-400">{item.warrantyPeriod} days warranty</p>
                        </td>
                        {/* Parts Price (procurement) — editable with save in edit mode */}
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <input
                              type="number"
                              min="0"
                              value={item.actualPrice - (item.marginalCost || 0)}
                              onChange={(e) => {
                                const procurement = parseFloat(e.target.value) || 0;
                                updateActualPrice(item.id, procurement + (item.marginalCost || 0));
                              }}
                              className="w-24 px-2 py-1 text-right border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {isEditMode && (
                              <button
                                onClick={() => saveOrderItemPrice(item.id, item.actualPrice - (item.marginalCost || 0))}
                                disabled={savingItemPriceId === item.id}
                                title="Save to order_items & services"
                                className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 transition-colors flex-shrink-0"
                              >
                                {savingItemPriceId === item.id
                                  ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600" />
                                  : <Save className="h-3.5 w-3.5" />
                                }
                              </button>
                            )}
                          </div>
                        </td>
                        {/* Marginal Price — read-only */}
                        <td className="px-4 py-3 text-right text-gray-600">
                          ₹{(item.marginalCost || 0).toLocaleString()}
                        </td>
                        {/* Final Price (combined) — read-only */}
                        <td className="px-4 py-3 text-right font-semibold text-blue-700">
                          ₹{item.actualPrice.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                            className="w-14 px-2 py-1 text-center border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          ₹{(item.actualPrice * item.quantity).toLocaleString()}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button onClick={() => removeFromCart(item.id)} className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals Summary */}
              <div className="flex justify-end mt-4">
                <div className="w-80 border border-gray-200 rounded-lg overflow-hidden text-sm">
                  <table className="w-full">
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-2.5 text-gray-500">Items Total</td>
                        <td className="px-4 py-2.5 text-right font-medium text-gray-800">₹{(totals.totalItemPrice || 0).toLocaleString()}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-2.5 text-gray-500">
                          <span className="block text-xs mb-0.5">Service Cost</span>
                          <input
                            type="number"
                            min="0"
                            value={editableServiceCost !== null ? editableServiceCost : totals.totalServiceCost}
                            onChange={(e) => setEditableServiceCost(parseFloat(e.target.value) || 0)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-gray-800 align-bottom">
                          ₹{(editableServiceCost !== null ? editableServiceCost : totals.totalServiceCost).toLocaleString()}
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-2.5 text-gray-500">
                          <span className="block text-xs mb-0.5">Discount</span>
                          <input
                            type="number"
                            min="0"
                            value={discountAmount === 0 ? '' : discountAmount}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              setDiscountAmount(isNaN(v) ? 0 : v);
                            }}
                            placeholder="0"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-red-500 align-bottom">
                          {discountAmount > 0 ? `–₹${discountAmount.toLocaleString()}` : '–'}
                        </td>
                      </tr>
                      {isGstInvoice ? (
                        <>
                          <tr className="border-b border-gray-100">
                            <td className="px-4 py-2 text-gray-500">CGST (9%)</td>
                            <td className="px-4 py-2 text-right text-gray-700">₹{((totals.gstAmount || 0) / 2).toFixed(2)}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="px-4 py-2 text-gray-500">SGST (9%)</td>
                            <td className="px-4 py-2 text-right text-gray-700">₹{((totals.gstAmount || 0) / 2).toFixed(2)}</td>
                          </tr>
                        </>
                      ) : (
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-2 text-gray-500">GST (18%)</td>
                          <td className="px-4 py-2 text-right text-gray-700">₹{(totals.gstAmount || 0).toFixed(2)}</td>
                        </tr>
                      )}
                      <tr className="bg-blue-600 text-white">
                        <td className="px-4 py-3 font-bold text-base">Total Amount</td>
                        <td className="px-4 py-3 text-right font-bold text-base">₹{Math.round(displayedFinalAmount || 0).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════ */}
      {/* Section 3 — Assign Technician (edit mode only)         */}
      {/* ══════════════════════════════════════════════════════ */}
      {isEditMode && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <SectionHeader num={3} title="Assign Technician" icon={<UserCheck className="h-4 w-4 text-emerald-600" />} color="bg-emerald-50" />
          <div className="p-6">
            {false && (
              <p className="text-sm text-gray-400 mb-3">Locked — payment has been received.</p>
            )}
            <div className="flex gap-3 max-w-lg">
              <select
                value={selectedTechnician || ''}
                onChange={(e) => setSelectedTechnician(e.target.value ? parseInt(e.target.value) : null)}
                className={`${inputCls} flex-1`}
              >
                <option value="">— Select Technician —</option>
                {technicians.map((tech) => (
                  <option key={tech.id} value={tech.id}>{tech.name} · {tech.specialization}</option>
                ))}
              </select>
              <button
                onClick={assignTechnician}
                disabled={!selectedTechnician}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════ */}
      {/* Section 4 — Update Order Status (edit mode only)       */}
      {/* ══════════════════════════════════════════════════════ */}
      {isEditMode && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <SectionHeader num={4} title="Update Order Status" icon={<Clock className="h-4 w-4 text-amber-600" />} color="bg-amber-50" />
          <div className="p-6">
            {false && (
              <p className="text-sm text-gray-400 mb-3">Locked — payment has been received.</p>
            )}
            <div className="flex flex-wrap gap-3 items-end max-w-2xl">
              <Field label="Order Status" className="flex-1 min-w-40">
                <select value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)} className={inputCls}>
                  <option value="Created">Created</option>
                  <option value="Assigned">Assigned</option>
                  <option value="Service InProgress">Service In Progress</option>
                  <option value="Service Done">Service Done</option>
                  <option value="Payment Received">Payment Received</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </Field>
              <Field label="Payment Method" className="flex-1 min-w-40">
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={inputCls}>
                  <option value="COD">Cash on Delivery (COD)</option>
                  <option value="Online">Online Payment</option>
                </select>
              </Field>
              <div className="pb-0.5">
                <button
                  onClick={updateOrderStatus}
                  disabled={!orderStatus}
                  className="px-5 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Replacements ── */}
      {isEditMode && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
            <SectionHeader num={5} title="Replacements" icon={<RefreshCw className="h-4 w-4 text-red-600" />} color="bg-red-50" />
            <div className="p-6">

              {/* Existing replacements */}
              {replacements.length > 0 && (
                <div className="space-y-3 mb-5">
                  {replacements.map((rep) => (
                    <div key={rep.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-xs text-gray-400 uppercase font-semibold block">Cost</span>
                            <span className="font-bold text-gray-800">₹{rep.replacementCost.toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-400 uppercase font-semibold block">Date</span>
                            <span className="text-gray-700">{new Date(rep.replacementDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-400 uppercase font-semibold block">Status</span>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                              rep.replacementStatus === 'Completed' ? 'bg-green-100 text-green-700' :
                              rep.replacementStatus === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                              rep.replacementStatus === 'Cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>{rep.replacementStatus}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-400 uppercase font-semibold block">Technician</span>
                            <span className="text-gray-700">{rep.technicianName || '—'}</span>
                          </div>
                          {rep.notes && (
                            <div className="col-span-2 sm:col-span-4">
                              <span className="text-xs text-gray-400 uppercase font-semibold block">Notes</span>
                              <span className="text-gray-600 text-xs">{rep.notes}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <button
                            onClick={() => sendReplacementMessage(rep.id!)}
                            title={rep.messageSent ? 'Message already sent' : 'Send WhatsApp message to customer'}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                              rep.messageSent
                                ? 'bg-green-50 text-green-600 border border-green-200 cursor-default'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                            disabled={rep.messageSent}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                            {rep.messageSent ? 'Sent' : 'Send Message'}
                          </button>
                          <button
                            onClick={() => startEditReplacement(rep)}
                            className="px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteReplacement(rep.id!)}
                            className="px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit form */}
              {showReplacementForm ? (
                <div className="border border-red-200 rounded-xl p-5 bg-red-50">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">{editingReplacementId ? 'Edit Replacement' : 'New Replacement'}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Field label="Replacement Cost (₹)">
                      <input
                        type="number"
                        value={replacementForm.replacementCost || ''}
                        onChange={(e) => setReplacementForm(f => ({ ...f, replacementCost: Number(e.target.value) }))}
                        className={inputCls}
                        placeholder="0"
                        min={0}
                      />
                    </Field>
                    <Field label="Replacement Date">
                      <input
                        type="date"
                        value={replacementForm.replacementDate || ''}
                        onChange={(e) => setReplacementForm(f => ({ ...f, replacementDate: e.target.value }))}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Technician">
                      <select
                        value={replacementForm.technicianId || ''}
                        onChange={(e) => {
                          const tech = technicians.find(t => t.id === Number(e.target.value));
                          setReplacementForm(f => ({ ...f, technicianId: tech?.id, technicianName: tech?.name }));
                        }}
                        className={inputCls}
                      >
                        <option value="">— Select Technician —</option>
                        {technicians.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Status">
                      <select
                        value={replacementForm.replacementStatus || 'Pending'}
                        onChange={(e) => setReplacementForm(f => ({ ...f, replacementStatus: e.target.value }))}
                        className={inputCls}
                      >
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </Field>
                    <Field label="Notes" className="sm:col-span-2">
                      <textarea
                        value={replacementForm.notes || ''}
                        onChange={(e) => setReplacementForm(f => ({ ...f, notes: e.target.value }))}
                        className={`${inputCls} resize-none`}
                        rows={2}
                        placeholder="Optional notes..."
                      />
                    </Field>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={saveReplacement}
                      disabled={replacementLoading}
                      className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {replacementLoading ? 'Saving...' : editingReplacementId ? 'Update' : 'Save Replacement'}
                    </button>
                    <button
                      onClick={() => { setShowReplacementForm(false); setEditingReplacementId(null); setReplacementForm({ replacementCost: 0, replacementDate: new Date().toISOString().split('T')[0], replacementStatus: 'Pending', notes: '' }); }}
                      className="px-5 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowReplacementForm(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add Replacement
                </button>
              )}
            </div>
          </div>
        )}
      {/* ── Fixed Footer ── */}
      <div className="fixed bottom-0 left-0 lg:left-64 right-0 z-20 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-screen-xl mx-auto px-6 py-3 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {cart.length > 0
              ? <><span className="font-semibold text-gray-800">{cart.length} service{cart.length > 1 ? 's' : ''}</span> selected · Total: <span className="font-bold text-blue-700">₹{Math.round(displayedFinalAmount || 0).toLocaleString()}</span></>
              : 'No services selected'
            }
          </p>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/orders')} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 shadow-sm"
            >
              {loading
                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                : <Save className="h-4 w-4 mr-2" />
              }
              {isEditMode ? 'Update Order' : 'Create Order'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrderPage;
